import { markRaw } from 'vue';
import { FooterItem, LinkItem } from '@/shared/domain/FooterItem';
import { FooterRepository } from '@/shared/domain/FooterRepository';
import FooterFeedback from '@/shared/infrastructure/components/footer/FooterFeedback.vue';
import FooterPrivacyChoices from '@/shared/infrastructure/components/footer/FooterPrivacyChoices.vue';
import { openPreferenceCenter } from '@/shared/infrastructure/consent/CookieYes';
import { useUrlHelper } from '@/page/infrastructure/composition/useUrlHelper';
import { ServicesApiResponse, useTranslation } from '@hfc/ui-core';

const { getBaseUrl, getEnrollmentAppUri, getIaFQuoteUrl } = useUrlHelper();
const { t } = useTranslation();

export class FooterMemory implements FooterRepository {

  public async getFooterItems (): Promise<ServicesApiResponse<Array<FooterItem>>> {
    const footerItems = [
      {
        title: 'common.coveredCalifornia',
        href: getBaseUrl(`/${t('links.coveredCalifornia')}`),
        links: [
          {
            label: 'common.getAQuote',
            href: getIaFQuoteUrl(),
          },
          {
            label: 'common.incomeLimits',
            href: getBaseUrl('/covered-california/income-limits'),
          },
          {
            label: 'common.openEnrollment',
            href: getBaseUrl('/covered-california-enrollment/open-enrollment'),
          },
          {
            label: 'common.deadlines',
            href: getBaseUrl('/covered-california/deadline'),
          },
          {
            label: 'common.qualifyingLifeEvents',
            href: getBaseUrl('/covered-california/life-events'),
          },
        ]
      },
      {
        title: 'common.carriers',
        href: '',
        links: [
          {
            label: 'Blue Shield',
            href: getBaseUrl('/covered-california/health-insurance-companies/blue-shield'),
          },
          {
            label: 'Kaiser Permanente',
            href: getBaseUrl('/covered-california/health-insurance-companies/kaiser'),
          },
          {
            label: 'Health Net',
            href: getBaseUrl('/covered-california/health-insurance-companies/health-net'),
          },
          {
            label: 'footer.viewAllCarriers',
            href: getBaseUrl('/covered-california/health-insurance-companies'),
          },
        ]
      },
      {
        title: 'common.metalTieredPlans',
        href: getBaseUrl('/covered-california/plans'),
        links: [
          {
            label: 'Bronze',
            href: getBaseUrl('/covered-california/plans/bronze'),
          },
          {
            label: 'Silver',
            href: getBaseUrl('/covered-california/plans/silver'),
          },
          {
            label: 'Gold',
            href: getBaseUrl('/covered-california/plans/gold-80'),
          },
          {
            label: 'Platinum',
            href: getBaseUrl('/covered-california/plans/platinum-90'),
          },
          {
            label: 'common.minimumCoverage',
            href: getBaseUrl('/covered-california/plans/minimum-coverage'),
          },
        ]
      },

      {
        title: 'footer.otherProducts',
        href: '',
        links: [
          {
            label: 'common.dental',
            href: getEnrollmentAppUri('/dental/quote'),
          },
          {
            label: 'common.vision',
            href: getEnrollmentAppUri('/vision/quote'),
          },
          {
            label: 'Medicare',
            href: getBaseUrl('/senior-health-insurance-options'),
          },
          {
            label: 'Medi-Cal',
            href: getBaseUrl('/covered-california/health-insurance-companies/medi-cal'),
          },
          {
            label: 'footer.shortTerm',
            href: getBaseUrl('/short-term-health-insurance'),
          },
          {
            label: 'common.smallBusiness',
            href: getBaseUrl('/group-health-insurance'),
          },
        ]
      },
    ];

    return {
      success: true,
      data: footerItems,
    };
  }

  public async getFooterExternalLinks (): Promise<ServicesApiResponse<Array<LinkItem>>> {
    const externalLinks = [
      {
        label: 'footer.aboutUs',
        href: getBaseUrl('/about-us'),
      },
      {
        label: 'footer.contactUs',
        href: getBaseUrl('/contact-us'),
      },
      {
        label: 'footer.reviews',
        href: getBaseUrl('/reviews'),
      },
      {
        label: 'footer.privacyChoices',
        href: getBaseUrl('/privacy-policy'),
        customComponent: markRaw(FooterPrivacyChoices),
        onClick: openPreferenceCenter,
        className: 'mb-0',
      },
      {
        label: 'footer.privacyPolicy',
        href: getBaseUrl('/privacy-policy'),
      },
      {
        label: 'footer.shareFeedback',
        href: '#',
        customComponent: markRaw(FooterFeedback),
        preventDefault: true,
      },
      {
        label: 'footer.siteMap',
        href: getBaseUrl('/sitemap'),
      },
      {
        label: 'Blog',
        href: getBaseUrl('/blog'),
      },
    ];

    return {
      success: true,
      data: externalLinks,
    };
  }

}
